import React, { useState } from 'react';
import { Copy, Download, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui/button';
import { ModalShell } from './ReviewActionModals';
import { eventUrlFor, partnerUrlFor, qrSrcFor } from './partnerHelpers';

// Shows a partner's referral QR + full URL for printing / sharing.
const PartnerQrModal = ({ partner, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const slug = partner.partnerReferralUrl;
  const isEvent = partner.partnerType === 'event';
  const url = isEvent ? eventUrlFor(slug) : partnerUrlFor(slug);
  // Distinct URL per branding state — otherwise the browser's cached copy
  // keeps showing the old style for up to a day after the flag flips.
  const qrVariant = partner.partnerQrIncludesLogo ? 'logo' : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    } catch (e) {
      toast.error('Could not copy — please copy it manually.');
    }
  };

  // The image is cross-origin (API host), so a plain <a download> would just
  // navigate — fetch to a blob and download the object URL instead.
  const downloadPng = async () => {
    setDownloading(true);
    try {
      const res = await fetch(qrSrcFor(slug, qrVariant));
      // Error responses are JSON bodies — without this check the "PNG" saves
      // fine and only fails at print time.
      if (!res.ok) throw new Error(`QR fetch failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${slug}-${isEvent ? 'event' : 'referral'}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error('Could not download the QR code.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ModalShell title={`QR code — ${partner.partnerName}`} onClose={onClose}>
      <div className="flex flex-col items-center">
        <img
          src={qrSrcFor(slug, qrVariant)}
          alt={`Referral QR code for ${partner.partnerName}`}
          width={200}
          height={200}
          className="rounded-lg border border-border bg-white p-2"
        />
        <Button
          variant="outline"
          className="mt-3"
          onClick={downloadPng}
          disabled={downloading}
        >
          {downloading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download PNG
        </Button>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isEvent ? 'Event link' : 'Referral link'}
        </label>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.target.select()}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {/* Intent-system action hover: neutral at rest, primary fill on
              hover — accent/muted hovers are invisible here since they share
              the same grey token. */}
          <Button
            variant="outline"
            className="shrink-0 transition-colors hover:bg-primary hover:text-primary-foreground"
            onClick={copyLink}
          >
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

export default PartnerQrModal;
